import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';
import { VaultStoreEffects } from '@app/root-store/vault-store/effects';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { DocumentService } from '@app/shared/services';
import { ToastrService } from 'ngx-toastr';
import { loaderTypes } from '@app/shared/component/loader/loader';
import * as loginSelectors from '@app/root-store/login-store/login.selector';
import * as selectorsRelationship from '@app/root-store/relationship-store/relationship.selector';
import { getWorkspaceItem } from '@app/root-store/workspaces-store/selector';
import { getAllProfessionalRelationShipSortSelector } from '@app/root-store/relationship-store/relationship.selector';
import { UserInviteComponent } from '@app/shared/component/user-invite/user-invite.component';
import { UserInviteSuccessScreenComponent } from '@app/modules/relationship/components/dialogs/user-invite-success-screen/user-invite-success-screen.component';

export interface AddTemplateDialogData {
  formsList: any[];
  vault_list: any[];
  file?: any;
  isEntity?: boolean;
  defaultVaultId?: number | string;
  vault_id?: number | string;
}

@AutoUnsubscribe()
@Component({
  selector: 'add-template-dialog',
  templateUrl: './add-template-dialog.component.html',
  styleUrls: ['./add-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddTemplateDialogComponent implements OnInit, OnDestroy {
  addTemplateForm: UntypedFormGroup;
  subscription = new Subscription();
  filteredTemplateTypes: any[] = [];
  filteredVaults: any[] = [];
  placeholderVaultOptions: any[] = [];
  selectedMembers: any[] = [];
  allWorkspaceMembers: any[] = [];
  allNonProfessionalExistingUsers: any[] = [];
  searchStringState: FormControl;
  connectionUserFormControl = new FormControl();
  currentUser: any;
  stateObject: any;
  forms: any[];
  vaults: any[];
  workspaceId: string | null = null;
  entityNameSection = true;
  membersSection = false;
  securityStateObject: any;
  readonly placeholderEntityId = 2197;
  readonly placeholderTooltip =
    "Use this placeholder entity if you don't own the entity. This will not create an entity template or an entity vault.";

  constructor(
    public dialogRef: MatDialogRef<AddTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddTemplateDialogData,
    private formBuilder: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private vaultService$: VaultStoreEffects,
    private vaultService: SharedVaultService,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private store: Store,
    private dialog: MatDialog,
    private router: Router
  ) {
    // For entities, template type and vault are not required
    const isEntity = this.data.isEntity || false;
    
    this.addTemplateForm = this.formBuilder.group({
      templateName: ['', Validators.required],
      templateType: [isEntity ? '' : '', isEntity ? [] : [Validators.required]],
      selectedVault: [isEntity ? '' : '', isEntity ? [] : [Validators.required]],
      placeholderVault: [''],
      vaultOwner: [''],
    });

    // Subscribe to vault state for entities
    if (isEntity) {
      this.subscription.add(
        this.vaultService$.vaultObject.subscribe((item) => {
          this.stateObject = item;
          this.forms = item.forms;
          this.vaults = item.vaults;
          this.placeholderVaultOptions = this.flattenVaultList(item?.vaults);
          
          // If placeholder entity is already selected, set the placeholder vault
          const currentTemplateType = this.addTemplateForm.get('templateType')?.value;
          if (currentTemplateType === this.placeholderEntityId && this.placeholderVaultOptions.length) {
            const preferredVaultId = this.getPreferredPlaceholderVaultId();
            if (preferredVaultId && !this.addTemplateForm.get('placeholderVault')?.value) {
              this.addTemplateForm.patchValue({
                placeholderVault: preferredVaultId,
              });
            } else if (!this.addTemplateForm.get('placeholderVault')?.value && this.placeholderVaultOptions[0]?.id) {
              this.addTemplateForm.patchValue({
                placeholderVault: this.placeholderVaultOptions[0].id,
              });
            }
          }
          
          this.cd.markForCheck();
        })
      );
    }

    // Pre-fill template type if file data is provided
    // Check for suggested_vault_form_id first (for AI suggestions), then fall back to id (for regular templates)
    const templateFormId = this.data.file?.suggested_vault_form_id || this.data.file?.id;
    if (templateFormId) {
      const templateType = this.data.formsList?.find((x) => x.id == templateFormId);
      if (templateType) {
        this.addTemplateForm.patchValue({
          templateType: templateType.id,
        });
      }
    }
  }

  ngOnInit(): void {
    const isEntity = this.data.isEntity || false;
    
    // Filter template types based on whether it's an entity or not
    if (isEntity) {
      // For entities, filter to show only entity templates (same as add-entity-dilaog)
      const allEntityTemplates = this.data.formsList?.filter((form) => 
        form.organization_section?.includes('Entities') || form.type?.toLowerCase().includes('entity')
      ) || [];
      
      // Separate placeholder entity and put it at the bottom
      const placeholderTemplate = allEntityTemplates.find(
        (item) => item?.id === this.placeholderEntityId || item?.type?.toLowerCase() === 'placeholder entity'
      );
      const otherTemplates = allEntityTemplates.filter(
        (item) => item?.id !== placeholderTemplate?.id && item?.id !== this.placeholderEntityId && item?.type?.toLowerCase() !== 'placeholder entity'
      );
      
      // Placeholder at the bottom
      this.filteredTemplateTypes = placeholderTemplate 
        ? [...otherTemplates, placeholderTemplate] 
        : otherTemplates;
    } else {
      // For non-entities, filter out 'Document' type
      this.filteredTemplateTypes = this.data.formsList?.filter((form) => form.type !== 'Document') || [];
    }
    
    this.filteredVaults = this.data.vault_list || [];
    
    // For entities, set the template type automatically from file data (same binding as add-entity-dilaog)
    if (isEntity) {
      const templateFormId = this.data.file?.suggested_vault_form_id || this.data.file?.id;
      if (templateFormId) {
        // Find in filteredTemplateTypes first, then fallback to full formsList
        let templateType = this.filteredTemplateTypes.find((x) => x.id == templateFormId);
        if (!templateType) {
          templateType = this.data.formsList?.find((x) => x.id == templateFormId);
        }
        if (templateType) {
          this.addTemplateForm.patchValue({
            templateType: templateType.id,
          });
          
          // If placeholder entity is selected, set the placeholder vault
          if (templateType.id === this.placeholderEntityId) {
            // Wait for placeholderVaultOptions to be populated from vault state subscription
            setTimeout(() => {
              if (this.placeholderVaultOptions.length) {
                const preferredVaultId = this.getPreferredPlaceholderVaultId();
                if (preferredVaultId) {
                  this.addTemplateForm.patchValue({
                    placeholderVault: preferredVaultId,
                  });
                } else if (this.placeholderVaultOptions[0]?.id) {
                  this.addTemplateForm.patchValue({
                    placeholderVault: this.placeholderVaultOptions[0].id,
                  });
                }
              }
              this.cd.detectChanges();
            }, 100);
          }
          
          this.cd.detectChanges();
        }
      }

      // Initialize entity-specific functionality
      this.searchStringState = new FormControl();
      this.initializeEntityForm();
    }
    
    // Set default vault - prioritize vault_id over defaultVaultId (only for non-entities)
    if (!isEntity) {
      const defaultVaultId = this.data.vault_id || this.data.defaultVaultId;
      if (defaultVaultId) {
        const defaultVault = this.filteredVaults.find((vault) => vault.id == defaultVaultId);
        if (defaultVault) {
          this.addTemplateForm.patchValue({
            selectedVault: defaultVault.id,
          });
          this.cd.detectChanges();
        }
      }
    }
  }

  initializeEntityForm(): void {
    // Subscribe to current user
    this.subscription.add(
      this.store.select(loginSelectors.getCurrentUserFullDetails).subscribe((res) => {
        this.currentUser = res;
        const creator = {
          id: this.currentUser?.id,
          user_id: this.currentUser?.id,
          memberUser: {
            id: this.currentUser?.id,
            name: this.currentUser?.name,
            user_profile_image_url: this.currentUser?.profile_image_url,
            hasGuardianPermissions: false,
          },
          type: 'owner',
          is_creator: true,
        };
        this.selectedMembers.unshift(creator);
        this.cd.markForCheck();
      })
    );

    // Subscribe to entity type changes for placeholder vault
    this.subscription.add(
      this.addTemplateForm.get('templateType').valueChanges.subscribe((entityType) => {
        const isPlaceholder = entityType === this.placeholderEntityId;
        const placeholderControl = this.addTemplateForm.get('placeholderVault');
        if (isPlaceholder) {
          placeholderControl.setValidators([Validators.required]);
          if (!placeholderControl.value && this.placeholderVaultOptions.length) {
            const preferredVaultId = this.getPreferredPlaceholderVaultId();
            if (preferredVaultId) {
              placeholderControl.setValue(preferredVaultId);
            } else {
              placeholderControl.setValue(this.placeholderVaultOptions[0]?.id);
            }
          }
        } else {
          placeholderControl.clearValidators();
          placeholderControl.setValue('');
        }
        placeholderControl.updateValueAndValidity({ emitEvent: false });
        this.cd.markForCheck();
      })
    );

    // Handle Professional user vault owner (will be set after currentUser is available)
    this.subscription.add(
      this.store.select(loginSelectors.getCurrentUserFullDetails).subscribe((user) => {
        if (user?.user_type === 'Professional' && this.router.url.includes('/workspace/')) {
          this.addTemplateForm.get('vaultOwner').setValidators([Validators.required]);
          this.addTemplateForm.get('vaultOwner').updateValueAndValidity();
          const split_url = this.router.url.split('/');
          this.workspaceId = split_url[2];
          this.subscription.add(
            this.store.select(getWorkspaceItem(this.workspaceId)).subscribe((data) => {
              this.allWorkspaceMembers = data.members
                .filter((x) => x.status === 'accept' && x.type === 'family')
                .map((x) => ({ ...x, ...x.userDetails }));
              this.cd.markForCheck();
            })
          );
        } else if (user?.user_type === 'Professional') {
          this.addTemplateForm.get('vaultOwner').clearValidators();
          this.addTemplateForm.get('vaultOwner').updateValueAndValidity();
          this.subscription.add(
            this.store.select(getAllProfessionalRelationShipSortSelector()).subscribe((data) => {
              this.allWorkspaceMembers = [
                {
                  ...user,
                  user_first_name: user.first_name,
                  user_last_name: user.first_middle_name,
                  user_id: user.id,
                  name: user?.name,
                },
                ...data.filter((x) => x.connection_status == 'approved').map((x) => ({ ...x, name: x?.user_first_name + ' ' + x?.user_last_name })),
              ];
              this.cd.markForCheck();
            })
          );
        }
      })
    );

    // Get non-professional existing users
    this.getAllNonProfessionalExistiongUsers();
  }

  getAllNonProfessionalExistiongUsers(): void {
    this.subscription.add(
      this.store.select(selectorsRelationship.getAndFilterNonProfessionalExcludeExistingUser('', [])).subscribe((data) => {
        const updatedObject = [];
        data.forEach((element) => {
          const tempEllemt = {
            ...element,
            id: element?.user_id,
            name: element?.user_first_name + ' ' + element?.user_last_name,
            type: 'member',
          };
          updatedObject.push(tempEllemt);
        });
        this.allNonProfessionalExistingUsers = updatedObject.map(({ id, user_id, name, relationship_name, user_profile_image_url }) => ({
          id,
          user_id,
          name,
          relationship_name,
          user_profile_image_url,
        }));
        this.cd.markForCheck();
      })
    );
  }

  get isPlaceholderSelected(): boolean {
    return this.addTemplateForm?.get('templateType')?.value === this.placeholderEntityId;
  }

  private flattenVaultList(vaults: any): any[] {
    if (!Array.isArray(vaults)) {
      return [];
    }
    return vaults.reduce((acc: any[], item: any) => {
      if (Array.isArray(item)) {
        acc.push(...item);
      } else if (item) {
        acc.push(item);
      }
      return acc;
    }, []);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  save(): void {
    if (this.addTemplateForm.valid) {
      const formValue = this.addTemplateForm.value;
      const isEntity = this.data.isEntity || false;

      if (isEntity && this.entityNameSection) {
        // If placeholder entity, create directly without members section
        if (this.isPlaceholderSelected) {
          this.createStandaloneVaultForEntity();
          return;
        }
        // Move to members section for non-placeholder entities
        this.nextItem(false);
        return;
      }

      if (isEntity && this.membersSection) {
        // Create vault and entity template
        this.createStandaloneVaultForEntity();
        return;
      }

      // For non-entities, close with form data
      const selectedTemplateType = this.data.formsList?.find((x) => x.id == formValue.templateType);
      const selectedVault = this.data.vault_list?.find((x) => x.id == formValue.selectedVault);

      this.dialogRef.close({
        status: 'success',
        templateName: formValue.templateName,
        templateType: selectedTemplateType,
        vault: selectedVault,
        vaultFormId: formValue.templateType || (this.data.file?.suggested_vault_form_id || this.data.file?.id),
        vault_id: formValue.selectedVault || this.data.vault_id,
        isEntity: isEntity,
        file: this.data.file,
        form_name_updated_by_user: true,
      });
    }
  }

  nextItem(finish: boolean): void {
    if (!finish && this.entityNameSection) {
      this.entityNameSection = false;
      this.membersSection = true;
      if (this.currentUser?.user_type === 'Professional') {
        if (this.selectedMembers.findIndex((x) => x.id === this.currentUser.id) === -1) {
          const memberUser = {
            id: this.currentUser.id ? this.currentUser.id : this.currentUser.user_id,
            user_id: this.currentUser.id ? this.currentUser.id : this.currentUser.user_id,
            from_user_id: this.currentUser.from_user_id,
            connection_status: this.currentUser.connection_status,
            relationship_name: this.currentUser.relationship_name,
            memberUser: this.currentUser,
            type: 'member',
          };
          this.selectedMembers = [...this.selectedMembers, memberUser];
        }
      }
      this.cd.detectChanges();
    }
    if (finish) {
      this.createStandaloneVaultForEntity();
    }
  }

  backItem(): void {
    if (this.membersSection) {
      this.membersSection = false;
      this.entityNameSection = true;
    }
    this.cd.detectChanges();
  }

  createStandaloneVaultForEntity(): void {
    if (this.isPlaceholderSelected) {
      const selectedVaultId = this.addTemplateForm.get('placeholderVault')?.value;
      if (!selectedVaultId) {
        this.toastr.error('Please select a vault for the placeholder entity.');
        return;
      }
      this.createEntityTemplate({ id: selectedVaultId, isPlaceholder: true });
      return;
    }
    const currentUserId = this.stateObject?.userId ? this.stateObject.userId : this.currentUser?.id;
    const payload = {
      name: this.addTemplateForm.get('templateName').value,
      type: 'entity',
      user_id: currentUserId,
      member: this.selectedMembers.map(({ user_id, type }) => ({ user_id, type })),
      ...(this.workspaceId && { workspace_id: this.workspaceId }),
    };
    this.subscription.add(
      this.vaultService.createVault(payload).subscribe(
        async (data) => {
          if (data) {
            await this.createEntityTemplate(data);
          }
        },
        (error: any) => {
          this.toastr.error(error.error.message);
        }
      )
    );
  }

  async createEntityTemplate(data): Promise<void> {
    const vaultId = data.id; // For placeholder, this is the selected vault ID; for new vault, this is the created vault ID
    const formData: any = await this.createformIdForFirstUploadDocument({
      currentUserId: this.stateObject?.userId ?? this.currentUser?.id,
      vaultFormId: this.addTemplateForm.get('templateType').value,
      vaultId: vaultId,
    });
    const updatedTemplateData = await this.updateFormTemplateData(formData.hub_vault_user_form_id);
    await this.updateUserFormsFormName(formData.hub_vault_user_form_id, vaultId);
    updatedTemplateData['hub_vault_user_form_id'] = formData.hub_vault_user_form_id;
    updatedTemplateData['vault_id'] = vaultId;

    this.dialogRef.close({
      status: 'success',
      templateName: this.addTemplateForm.get('templateName').value,
      templateType: this.data.formsList?.find((x) => x.id == this.addTemplateForm.get('templateType').value),
      vault: { id: vaultId },
      vaultFormId: this.addTemplateForm.get('templateType').value,
      vault_id: vaultId,
      isEntity: true,
      file: this.data.file,
      form_name_updated_by_user: true,
      entityResult: { status: 'success', data: updatedTemplateData, id: formData.hub_vault_user_form_id },
    });
  }

  createformIdForFirstUploadDocument(argument): Promise<any> {
    return new Promise((resolve, reject) => {
      const data = {
        user_id: argument.currentUserId,
        vault_form_id: argument.vaultFormId,
        vaultId: argument.vaultId,
        url: this.workspaceId ? `/workspace/${this.workspaceId}/vault` : this.router.url,
      };
      this.subscription.add(
        this.documentService.getforIdForFirstUploadDocument(data, loaderTypes.mainAnimation).subscribe(
          (res) => {
            try {
              this.securityStateObject = res['vault_user_form_id'].public_key;
            } catch (error) {}
            if (res.vault_user_form_id.vault_user_form_id) {
              resolve({ vault_form_id: argument.vaultFormId, hub_vault_user_form_id: res.vault_user_form_id.vault_user_form_id, argument });
            }
          },
          (error) => {
            this.toastr.error(error.error?.message || 'An error occurred');
            reject(error);
          }
        )
      );
    });
  }

  updateFormTemplateData(vault_user_form_id, isShowLoader = true): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const dynamicFieldsValue = {
        foreignDisregardedEntityFullLegalNameofEntity: this.addTemplateForm.get('templateName')?.value ?? '',
      };
      const payload = {
        id: vault_user_form_id,
        status_progress: 'need to add',
        user_id: this.stateObject?.userId ?? this.currentUser?.id,
        form_data: {
          beneFicaryForm: [],
          dynamicFieldsForm: dynamicFieldsValue,
          showNotes: undefined,
        },
        user_pk: this.securityStateObject?.public_key,
      };
      this.subscription.add(
        this.documentService.updateFormTampletData(payload, null, undefined, undefined, false, undefined, isShowLoader).subscribe(
          (res) => {
            resolve(res);
          },
          (error) => {
            this.toastr.error(error.error?.message || 'An error occurred');
            reject(error);
          }
        )
      );
    });
  }

  updateUserFormsFormName(hub_vault_user_form_id, vaultId): Promise<any> {
    const entityName = this.addTemplateForm.get('templateName').value;
    const current_template = this.forms?.find((x) => x.id === this.addTemplateForm.get('templateType').value);
    return new Promise((resolve) => {
      this.subscription.add(
        this.documentService
          .saveNameWithAction(
            {
              form_name: entityName,
              form_name_updated_by_user: true,
              id: hub_vault_user_form_id,
              naming_convention: current_template?.naming_convention,
              dynamicFieldsForm: { foreignDisregardedEntityFullLegalNameofEntity: entityName },
              user_id: this.stateObject?.userId ?? this.currentUser?.id,
              current_template: current_template,
              filterArrayItems: [],
              vault_id: vaultId || this.addTemplateForm.get('placeholderVault').value || this.stateObject?.vaults?.[0]?.id,
              vault_list: this.vaults,
            },
            true
          )
          .subscribe(
            (res) => {
              resolve(res);
            },
            (error) => {
              this.toastr.error(error.error?.message || 'An error occurred');
            }
          )
      );
    });
  }

  onSelectChange(event: MatSelectChange): void {
    const selectedUserDetails = this.allWorkspaceMembers.filter((user) => user.user_id === this.addTemplateForm.controls['vaultOwner'].value)[0];
    this.selectedMembers = this.selectedMembers.filter((member) => !member.is_creator);
    const creator = {
      id: selectedUserDetails?.user_id,
      user_id: selectedUserDetails?.user_id,
      memberUser: {
        id: selectedUserDetails?.user_id,
        name: selectedUserDetails?.name,
        user_profile_image_url: selectedUserDetails.userDetails?.profile_image_url,
        hasGuardianPermissions: false,
      },
      type: 'owner',
      is_creator: true,
    };
    this.selectedMembers.unshift(creator);
  }

  selectConnectedUser(user): void {
    const memberUser = {
      id: user.id ? user.id : user.user_id,
      user_id: user.id ? user.id : user.user_id,
      from_user_id: user.from_user_id,
      connection_status: user.connection_status,
      relationship_name: user.relationship_name,
      memberUser: user,
      type: 'member',
    };
    this.selectedMembers = [...this.selectedMembers, memberUser];
    this.cd.markForCheck();
  }

  deleteMember(user): void {
    this.selectedMembers = this.selectedMembers.filter((item) => item.id !== user.id);
    this.cd.markForCheck();
  }

  openInviteDialog(): void {
    const data = {
      disableSelectionOption: true,
      custom_user_type: 'normal',
      isSharedVault: true,
    };
    const modalRef = this.dialog.open(UserInviteComponent, {
      data: data,
      autoFocus: false,
      panelClass: 'full-window-dialog-2-mobile-responsive',
    });
    this.subscription.add(
      modalRef.afterClosed().subscribe(
        (res) => {
          res['user'].user_id = res.user.to_user_id ? res.user.to_user_id : res.user.id;
          this.selectConnectedUser(res.user);
          if (res && res['success'] == true) {
            if (res['isexistingUser']) {
              this.toastr.success(`Connection request has been sent to ${res.FullName}`);
            } else {
              this.dialog.open(UserInviteSuccessScreenComponent, {
                autoFocus: false,
                panelClass: 'full-window-dialog-2',
                data: res,
              });
            }
          }
        },
        (error) => {
          this.toastr.error('Error in inviting user', error.error.message);
        }
      )
    );
  }

  onClearSearchInput(): void {
    this.searchStringState.setValue('');
    this.cd.detectChanges();
  }

  isUserDisabledToSkip(): boolean {
    if (this.entityNameSection) {
      const isPlaceholder = this.isPlaceholderSelected;
      const isProfessional = this.currentUser?.user_type === 'Professional';
      if (
        this.addTemplateForm.get('templateName')?.value === '' ||
        this.addTemplateForm.get('templateType')?.value === '' ||
        (isPlaceholder ? this.addTemplateForm.get('placeholderVault')?.value === '' : false) ||
        (isProfessional && !isPlaceholder ? this.addTemplateForm.get('vaultOwner')?.value === '' : false)
      ) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  isFormValid(): boolean {
    if (this.data.isEntity && this.membersSection) {
      return true; // Allow proceeding to create vault
    }
    if (this.data.isEntity && this.entityNameSection && this.isPlaceholderSelected) {
      // For placeholder entities, validate form including placeholder vault
      return this.addTemplateForm.valid;
    }
    return this.addTemplateForm.valid;
  }

  closeDialog(): void {
    this.dialogRef.close();
  }


  selectAllContent($event): void {
    $event.target.select();
  }

  disableWhiteSpaceOnFirstCharacter(event): void {
    if (event.target.selectionStart == 0 && (event.keyCode == 32 || event.keyCode == 9)) {
      event.preventDefault();
    }
  }

  getSelectedTemplateTypeName(): string {
    const selectedId = this.addTemplateForm.get('templateType')?.value;
    if (selectedId) {
      // First check filteredTemplateTypes
      let selectedTemplate = this.filteredTemplateTypes.find((item) => item.id == selectedId);
      // If not found and it's an entity, check the full formsList
      if (!selectedTemplate && this.data.isEntity) {
        selectedTemplate = this.data.formsList?.find((item) => item.id == selectedId);
      }
      return selectedTemplate?.type || (this.data.isEntity ? 'Entity' : '');
    }
    return this.data.isEntity ? 'Entity' : '';
  }

  getSelectedVaultName(): string {
    const selectedId = this.addTemplateForm.get('selectedVault')?.value;
    if (selectedId) {
      const selectedVault = this.filteredVaults.find((vault) => vault.id == selectedId);
      return selectedVault?.name || '';
    }
    return '';
  }

  getSelectedPlaceholderVaultName(): string {
    const selectedId = this.addTemplateForm.get('placeholderVault')?.value;
    if (selectedId) {
      const selectedVault = this.placeholderVaultOptions.find((vault) => vault.id == selectedId);
      return selectedVault?.name || '';
    }
    return '';
  }

  private getPreferredPlaceholderVaultId(): any {
    // Check if vault_id is provided in data
    if (this.data.vault_id) {
      const matchedVault = this.placeholderVaultOptions?.find((vault) => String(vault?.id) === String(this.data.vault_id));
      if (matchedVault) {
        return matchedVault.id;
      }
      return this.data.vault_id;
    }
    // Return first available vault as fallback
    return this.placeholderVaultOptions?.[0]?.id ?? null;
  }
}

